import _ from 'lodash';
import store from '@/redux/store';
import { NotificationManager } from 'react-notifications';

/**
 * Kiểm tra xem user hiện tại có quyền thực hiện hành động trong một nhóm quyền cụ thể không.
 *
 * @param {string} permissionGroup - Tên nhóm quyền (module), ví dụ: 'customer'
 * @param {string} current - Tên hành động cần kiểm tra, ví dụ: 'create', 'list'
 * @returns {boolean} Trả về true nếu có quyền hoặc là ADMIN
 */
export function checkPermission(permissionGroup, current) {
    const appState = store.getState();
    let authUser = appState.auth.authUser;

    if (!authUser.permissions[permissionGroup]) return false;
    if (!authUser.permissions[permissionGroup].length) return false;

    // var permissions = Object.keys(userPermissions);

    if (authUser.permissions[permissionGroup].indexOf(current) >= 0 || authUser.role_code === 'ADMIN') return true;
    else return false;
}

/**
 * Gom các quyền lại thành nhóm theo module (dựa vào phần thứ hai của route_name).
 * Sau đó chia nhỏ thành từng mảng con (chunk) để dễ hiển thị dạng grid (mỗi dòng 4 nhóm).
 *
 * @param {Array} permissions - Mảng các quyền, mỗi quyền chứa thuộc tính route_name dạng 'module.action'
 * @returns {Array[][][]} Mảng các nhóm quyền đã được chunk theo nhóm + cột
 */
export function chunkGroup(permissions) {
    // group permissions
    var groups = [];
    permissions.forEach((permission, index) => {
        if (index === 0) groups.push([permission]);
        else {
            let isPushed = false;

            groups.forEach((group) => {
                if (group[0].route_name.split('.')[1] === permission.route_name.split('.')[1]) {
                    group.push(permission);
                    isPushed = true;
                }
            })
            // if not match any group
            if (!isPushed) groups.push([permission]);
        }
    });

    groups = _.chunk(groups, 4);

    return groups;
}

/**
 * Kiểm tra user có phải là admin không (dựa vào role_code).
 *
 * @param {Object} authUser - Đối tượng user
 * @returns {boolean} True nếu user là admin
 */
export const isAdmin = (authUser) => {
    return authUser?.role_code === 'ADMIN';
};


/**
 * Kiểm tra user có quyền thực hiện một hoặc nhiều hành động trong một module cụ thể hay không.
 *
 * @param {Object} authUser - Đối tượng user
 * @param {string} permissionKey - Tên module quyền, ví dụ: 'customer'
 * @param {string} actionString - Chuỗi hành động, ví dụ: 'list', 'create,update'
 * @returns {boolean} True nếu user có ít nhất một quyền trong số đó hoặc là ADMIN
 */
export const hasPermission = (authUser, permissionKey, actionString = 'list') => {
    if (!authUser || !permissionKey) return false;
    if (isAdmin(authUser)) return true;

    const permissions = authUser.permissions || {};
    const userActions = permissions[permissionKey] || [];

    // Nếu có nhiều action phân tách bằng dấu phẩy → kiểm tra ít nhất một đúng
    const actions = actionString.split(',').map(a => a.trim());
    return actions.some(action => userActions.includes(action));
};

/**
 * Kiểm tra menu item có nên hiển thị không, dựa vào quyền user.
 *
 * @param {Object} authUser - Đối tượng user
 * @param {Object} item - Đối tượng menu item chứa các field như: permission, isAdminOnly
 * @returns {boolean} True nếu nên hiển thị
 */
export const canShowMenuItem = (authUser, item) => {
    // Ẩn nếu menu này chỉ dành cho admin
    if (item.isAdminOnly && !isAdmin(authUser)) return false;

    // Không yêu cầu permission cụ thể thì hiển thị
    if (!item.permission) return true;

    // Nếu có permission thì phải có quyền "list"
    return hasPermission(authUser, item.permission);
};


/**
 * Gói một Redux thunk action để chỉ thực hiện nếu user có quyền.
 * Nếu không có quyền, sẽ hiện cảnh báo và không dispatch action.
 *
 * @param {Function} thunkFn - Hàm thunk gốc có dạng: filter => (dispatch, getState) => { ... }
 * @param {Object} options - Thông tin quyền cần kiểm tra.
 * @param {string} options.permission - Tên module quyền (ví dụ: 'customer', 'order').
 * @param {string} [options.action='list'] - Tên hành động (mặc định là 'list', có thể là 'create', 'remove', 'update', v.v.).
 *
 * @returns {Function} Một thunk mới, chỉ thực thi nếu user có quyền.
 *
 * @example
 * // Giả sử có một thunk loadCustomerList
 * const loadCustomerList = (filter) => (dispatch, getState) => {
 *   dispatch({ type: 'CUSTOMER_LIST_LOADING' });
 *   return api.get('/customers', { params: filter }).then((res) => {
 *     dispatch({ type: 'CUSTOMER_LIST_SUCCESS', payload: res.data });
 *   });
 * };
 *
 * // Gói lại với quyền kiểm tra: chỉ chạy nếu user có quyền 'customer.list'
 * const loadCustomerListWithPermission = withPermission(loadCustomerList, {
 *   permission: 'customer',
 *   action: 'list'
 * });
 *
 * // Cách dùng trong component hoặc mapDispatchToProps
 * dispatch(loadCustomerListWithPermission({ page: 1 }));
 * const mapDispatchToProps = (dispatch) => ({
 *   getListCustomer: (filter) => dispatch(
 *       withPermission(getListCustomer, { permission: 'customer', action: 'list' })(filter)
 *   ),
 *});
 */
export const withPermission = (thunkFn, { permission, action = 'list' }) => {
    return (filter) => (dispatch, getState) => {
        const authUser = getState().auth.authUser;

        if (hasPermission(authUser, permission, action)) {
            return thunkFn(filter)(dispatch, getState);
        } else {
            const message = `Bạn không có quyền "${action}" trong module "${permission}".`;
            console.warn(`Bị chặn: ${message}`);
            NotificationManager.warning(message);
            return Promise.resolve(); // tránh lỗi Promise chain
        }
    };
};